import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Shared right-sidebar width for Generate + Editor workspaces. */
export const WORKSPACE_SIDEBAR_WIDTH_PX = 360;

export function AiWorkspaceLayout({
  header,
  main,
  sidebar,
  sidebarHeader,
  fullHeight = true,
}: {
  header?: ReactNode;
  main: ReactNode;
  sidebar: ReactNode;
  sidebarHeader?: ReactNode;
  /** When true, root is `h-screen` (editor). When false, `min-h-screen` (generate). */
  fullHeight?: boolean;
}) {
  const rootClass = fullHeight ? 'h-screen' : 'min-h-screen';

  return (
    <div className={`${rootClass} bg-background text-foreground flex flex-col`}>
      {header}
      <div
        className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_360px]"
        dir="ltr"
      >
        {/* Main: preview / canvas — left on desktop, above sidebar on mobile. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:col-start-1 lg:row-start-1 lg:min-h-0">
          {main}
        </div>

        {/* Sidebar: AI controls — right on desktop, below main on mobile. */}
        <aside className="bg-card border-border flex max-h-full min-h-0 w-full max-w-full flex-shrink-0 flex-col border-t lg:col-start-2 lg:row-start-1 lg:border-t-0 lg:border-l">
          {sidebarHeader}
          <div className="min-h-0 flex-1 overflow-y-auto p-5">{sidebar}</div>
        </aside>
      </div>
    </div>
  );
}

export function WorkspaceTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        'h-auto flex-1 rounded-none border-transparent border-b-2 px-4 py-3 shadow-none hover:bg-accent/80',
        active
          ? 'text-foreground border-primary -mb-px font-medium'
          : 'text-muted-foreground hover:text-foreground border-transparent',
      )}
    >
      {children}
    </Button>
  );
}

export function WorkspaceSidebarNav({ children }: { children: ReactNode }) {
  return <nav className="border-border flex flex-shrink-0 border-b text-sm">{children}</nav>;
}

/** Static sidebar title (e.g. Generate page with a single AI panel). */
export function WorkspaceSidebarTitle({ children }: { children: ReactNode }) {
  return (
    <WorkspaceSidebarNav>
      <span className="border-primary text-foreground flex-1 -mb-px border-b-2 px-4 py-3 font-medium">
        {children}
      </span>
    </WorkspaceSidebarNav>
  );
}
